/**
 * Asset Management — the shapes the four screens read, and the arithmetic
 * behind them.
 *
 * The platform computes all of this (`backend/api/slt/assets/lifecycle.py`);
 * the functions here are the same rules, used by the sample data so the
 * demonstration shows the numbers the platform would, and tested against the
 * same edge cases. If one changes, both change.
 *
 *   health   condition (or 100) − open defects (≤60) − age past 60% of design
 *            life (≤25) − predicted failure (≤30); none at all when nothing is
 *            known, never 100 by default
 *   band     5 very good (80+) · 4 good · 3 fair · 2 poor · 1 very poor (<20)
 *   risk     criticality × (6 − band), 1–25
 */
import { t } from "../i18n";
import type { AmSurvey } from "./entryForms";

export type LifecycleStage =
  "planned" | "commissioning" | "in_service" | "degraded" | "standby" | "decommissioned" | "disposed";

export const STAGES: LifecycleStage[] = [
  "planned",
  "commissioning",
  "in_service",
  "degraded",
  "standby",
  "decommissioned",
  "disposed",
];

export const STAGE_LABEL: Record<LifecycleStage, string> = {
  planned: "Planned",
  commissioning: "Commissioning",
  in_service: "In service",
  degraded: "Degraded",
  standby: "Standby",
  decommissioned: "Decommissioned",
  disposed: "Disposed",
};

export type DepreciationMethod = "straight_line" | "declining_balance" | "none";

export const METHOD_LABEL: Record<DepreciationMethod, string> = {
  straight_line: "Straight line",
  declining_balance: "Declining balance",
  none: "Not depreciated",
};

export const BAND_LABEL: Record<number, string> = {
  5: "Very good",
  4: "Good",
  3: "Fair",
  2: "Poor",
  1: "Very poor",
};

export const BAND_COLOUR: Record<number, string> = {
  5: "var(--go)",
  4: "var(--grade-b)",
  3: "var(--caution)",
  2: "var(--p2)",
  1: "var(--nogo)",
};

/* ---- shapes ------------------------------------------------------------- */

export interface AmZone {
  id: string;
  parentId: string | null;
  code: string;
  name: string;
  level: number | null;
  zoneType: string;
  assets: number;
}

export interface AmAssetRow {
  id: string;
  tag: string;
  name: string;
  assetType: string;
  assetTypeName: string;
  zoneId: string | null;
  zoneCode: string | null;
  parentId: string | null;
  criticality: number;
  status: string;
  stage: LifecycleStage;
  conditionScore: number | null;
  healthScore: number | null;
  healthBand: number | null;
  installedOn: string | null;
  manufacturer: string | null;
  model: string | null;
  serialNo: string | null;
  children: number;
}

export interface AmRegister {
  site: { id: string; code: string; name: string };
  zones: AmZone[];
  types: { key: string; name: string; category: string | null; assets: number }[];
  assets: AmAssetRow[];
  total: number;
  truncated: boolean;
  coverage: { assets: number; dated: number; assessed: number; withMaker: number; components: number };
}

export interface AmFilter {
  q?: string;
  assetType?: string;
  zoneId?: string;
  stage?: string;
}

export interface AmLife {
  designLifeYears: number | null;
  lifeSource: "asset" | "policy" | null;
  replacementValue: number | null;
  valueSource: "asset" | "policy" | null;
  ageYears: number | null;
  remainingYears: number | null;
  consumedPct: number | null;
  endOfLife: string | null;
  bookValue: number | null;
  warrantyDaysLeft: number | null;
}

export interface AmAsset {
  id: string;
  siteId: string;
  tag: string;
  name: string;
  assetType: string;
  assetTypeName: string;
  category: string | null;
  conditionModel: string | null;
  zoneCode: string | null;
  zoneName: string | null;
  parentId: string | null;
  parentTag: string | null;
  criticality: number;
  status: string;
  stage: LifecycleStage;
  conditionScore: number | null;
  /** build 6B: when the score was surveyed, what it rests on, and who entered it */
  conditionAt?: string | null;
  conditionBasis?: string | null;
  conditionBy?: string | null;
  healthScore: number | null;
  healthBand: number | null;
  healthBasis: Record<string, unknown> | null;
  installedOn: string | null;
  manufacturer: string | null;
  model: string | null;
  serialNo: string | null;
  purchasedOn: string | null;
  purchaseCost: number | null;
  replacementValue: number | null;
  designLifeYears: number | null;
  warrantyUntil: string | null;
  disposedOn: string | null;
  depreciationMethod: DepreciationMethod;
  residualPct: number;
  attrs: Record<string, unknown>;
}

export interface AmPlan {
  id: string;
  assetId?: string;
  tag?: string;
  assetName?: string;
  assetType?: string;
  criticality?: number;
  title: string;
  trigger: "calendar" | "usage";
  intervalDays: number | null;
  usageMeasurement: string | null;
  intervalUsage: number | null;
  leadDays?: number;
  priority: number;
  estimatedHours?: number | null;
  estimatedCost?: number | null;
  tasks?: string[];
  active: boolean;
  lastDoneAt: string | null;
  nextDueAt: string | null;
  state: "overdue" | "due" | "scheduled" | "waiting" | "paused";
  openWorkOrder?: string | null;
}

export interface AmAssetDetail {
  asset: AmAsset;
  zonePath: { code: string; name: string }[];
  children: { id: string; tag: string; name: string; assetType: string; healthBand: number | null; stage: string }[];
  workOrders: {
    id: string;
    number: string;
    kind: string;
    status: string;
    priority: number;
    title: string;
    openedAt: string;
    completedAt: string | null;
    costActual: number | null;
  }[];
  defects: {
    id: string;
    defectType: string | null;
    severity: number;
    status: string;
    summary: string | null;
    detectedAt: string;
  }[];
  readings: {
    measurement: string;
    unit: string | null;
    value: number | null;
    observedAt: string;
    source: string | null;
  }[];
  predictions: {
    modelKey: string;
    horizonDays: number | null;
    predictedValue: number | null;
    probability: number | null;
    confidence: number | null;
    rulP50Days: number | null;
    predictedAt: string;
  }[];
  documents: { id: string; title: string; relation: string | null }[];
  plans: AmPlan[];
  healthHistory: { observedOn: string; score: number; band: number }[];
  life: AmLife;
  policy: AmPolicy | null;
  reliability: Reliability;
  costToDate: number;
}

export interface Reliability {
  failures: number;
  mtbfDays: number | null;
  mttrHours: number | null;
}

export interface AmRisk {
  id: string;
  tag: string;
  name: string;
  assetType: string;
  zoneCode: string | null;
  criticality: number;
  healthScore: number;
  healthBand: number;
  risk: number;
  basis: Record<string, unknown> | null;
}

export interface AmCondition {
  assets: number;
  assessed: number;
  unassessed: number;
  /** [criticality − 1][band − 1] */
  matrix: number[][];
  bands: Record<string, number>;
  topRisks: AmRisk[];
  highRisk: number;
  byType: {
    assetType: string;
    name: string;
    assets: number;
    assessed: number;
    meanHealth: number | null;
    poor: number;
  }[];
  trend: { month: string; meanHealth: number; assets: number }[];
  /** build 6B: assets with a surveyed condition score, and the latest surveys entered */
  surveyed: number;
  recentSurveys: AmSurvey[];
  method: string;
}

export interface AmBadActor extends Reliability {
  id: string;
  tag: string;
  name: string;
  assetType: string;
  criticality: number;
  cost: number;
}

export interface AmMaintenance {
  plans: AmPlan[];
  summary: {
    plans: number;
    active: number;
    overdue: number;
    due: number;
    due30Days: number;
    compliancePct: number | null;
    pmCompleted12m: number;
    pmOnTime12m: number;
    plannedSharePct: number | null;
  };
  workMix12m: Record<string, { count: number; cost: number }>;
  badActors: AmBadActor[];
  coverage: { workOrders24m: number; linkedToAsset: number };
  method: string;
}

export interface AmPlanInput {
  assetId: string;
  title: string;
  trigger: "calendar" | "usage";
  intervalDays?: number;
  usageMeasurement?: string;
  intervalUsage?: number;
  firstDueOn?: string;
  leadDays?: number;
  priority?: number;
  estimatedHours?: number;
  estimatedCost?: number;
  tasks?: string[];
}

export interface AmPolicy {
  assetType: string;
  designLifeYears: number | null;
  replacementCost: number | null;
  currency: string;
  note: string | null;
}

export interface AmForecastYear {
  year: number;
  count: number;
  cost: number;
  critical: number;
  uncosted: number;
  funded: number | null;
}

export interface AmLifecycleRow extends AmLife {
  id: string;
  tag: string;
  name: string;
  assetType: string;
  assetTypeName: string;
  criticality: number;
  stage: LifecycleStage;
  healthBand: number | null;
}

export interface AmLifecycle {
  coverage: {
    assets: number;
    dated: number;
    lifeKnown: number;
    lifeFromPolicy: number;
    valued: number;
    valueFromPolicy: number;
    forecastable: number;
  };
  totals: {
    replacementValue: number;
    bookValue: number;
    valuedAssets: number;
    bookedAssets: number;
    pastEndOfLife: number;
  };
  stages: Record<string, number>;
  forecast: AmForecastYear[];
  warranties: {
    expiring90: { id: string; tag: string; name: string; warrantyUntil: string; daysLeft: number }[];
    expired: number;
    inWarranty: number;
  };
  assets: AmLifecycleRow[];
  policies: AmPolicy[];
  method: string;
}

export interface AmPatch {
  name?: string;
  zoneId?: string | null;
  attrs?: Record<string, unknown>;
  criticality?: number;
  lifecycleStage?: LifecycleStage;
  parentId?: string | null;
  installedOn?: string | null;
  manufacturer?: string | null;
  model?: string | null;
  serialNo?: string | null;
  purchasedOn?: string | null;
  purchaseCost?: number | null;
  replacementValue?: number | null;
  designLifeYears?: number | null;
  warrantyUntil?: string | null;
  disposedOn?: string | null;
  depreciationMethod?: DepreciationMethod;
  residualPct?: number;
}

/** An asset type the tenant's packs install, with the attribute schema its form is built from. */
export interface AmTypeDef {
  key: string;
  name: string;
  category: string | null;
  geometryKind: string | null;
  conditionModel: string | null;
  schema: AttrSchema;
}

/** The subset of JSON Schema a pack uses for asset attributes. */
export interface AttrSchema {
  type?: string;
  required?: string[];
  properties?: Record<string, AttrProp>;
}

export interface AttrProp {
  type?: string | string[];
  enum?: (string | number)[];
  format?: string;
  minimum?: number;
  maximum?: number;
  title?: string;
  description?: string;
}

export interface AmCreateInput {
  assetType: string;
  tag: string;
  name: string;
  zoneId?: string | null;
  parentId?: string | null;
  criticality: number;
  lifecycleStage: LifecycleStage;
  installedOn?: string | null;
  manufacturer?: string | null;
  model?: string | null;
  serialNo?: string | null;
  purchasedOn?: string | null;
  purchaseCost?: number | null;
  replacementValue?: number | null;
  designLifeYears?: number | null;
  warrantyUntil?: string | null;
  attrs: Record<string, unknown>;
}

export type AmHistoryKind = "created" | "changed" | "work_order" | "work_done" | "defect" | "defect_resolved" | "note";

export interface AmHistoryEvent {
  at: string;
  kind: AmHistoryKind;
  who: string | null;
  title: string;
  changes?: { field: string; before: unknown; after: unknown }[];
  detail?: Record<string, unknown>;
}

export type NoteCategory = "observation" | "recommendation" | "handover" | "follow_up";

export const NOTE_CATEGORY_LABEL: Record<NoteCategory, string> = {
  observation: "Observation",
  recommendation: "Recommendation",
  handover: "Handover",
  follow_up: "Follow-up",
};

export interface AmNote {
  id: string;
  category: NoteCategory;
  body: string;
  createdAt: string;
  createdBy: string | null;
}

/** A field name as a person reads it: "attrs.rated_hours" → "Rated hours". */
export function fieldLabel(field: string): string {
  const key = field.startsWith("attrs.") ? field.slice(6) : field;
  const known: Record<string, string> = {
    zone_id: "Zone",
    parent_id: "Parent asset",
    lifecycle_stage: "Lifecycle stage",
    serial_no: "Serial number",
    installed_on: "Installed",
    purchased_on: "Purchased",
    purchase_cost: "Purchase cost",
    replacement_value: "Replacement value",
    design_life_years: "Design life",
    warranty_until: "Warranty until",
    depreciation_method: "Depreciation",
    residual_pct: "Residual value (%)",
    asset_type: "Type",
  };
  if (known[key]) return known[key];
  const words = key.replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Check type attributes against the pack's schema before sending, so the form
 * can point at the field — the platform checks again and has the last word.
 */
export function attrProblems(schema: AttrSchema, attrs: Record<string, unknown>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const req of schema.required ?? []) {
    const v = attrs[req];
    if (v === undefined || v === null || v === "") out[req] = t("Required for this type.");
  }
  for (const [key, prop] of Object.entries(schema.properties ?? {})) {
    const v = attrs[key];
    if (v === undefined || v === null || v === "") continue;
    const types = Array.isArray(prop.type) ? prop.type : prop.type ? [prop.type] : [];
    if ((types.includes("number") || types.includes("integer")) && typeof v !== "number")
      out[key] = t("Must be a number.");
    else if (typeof v === "number" && prop.minimum !== undefined && v < prop.minimum)
      out[key] = t("At least {n}.", { n: prop.minimum });
    else if (typeof v === "number" && prop.maximum !== undefined && v > prop.maximum)
      out[key] = t("At most {n}.", { n: prop.maximum });
    else if (prop.enum && !prop.enum.includes(v as string | number)) out[key] = t("Choose one of the listed values.");
  }
  return out;
}

export interface AmImportRow extends AmPatch {
  tag: string;
}

export interface AmImportResult {
  updated: number;
  notFound: string[];
  notFoundCount: number;
  errors: { tag: string; error: string }[];
}

/* ---- the arithmetic (mirrors lifecycle.py) ------------------------------ */

const DEFECT_PENALTY: Record<number, number> = { 5: 40, 4: 25, 3: 12, 2: 5, 1: 2 };

export function bandOf(score: number): number {
  if (score >= 80) return 5;
  if (score >= 60) return 4;
  if (score >= 40) return 3;
  if (score >= 20) return 2;
  return 1;
}

export function riskOf(criticality: number, band: number): number {
  return criticality * (6 - band);
}

const DAY = 86_400_000;
export function yearsBetween(from: string, to: Date): number {
  return (to.getTime() - new Date(from).getTime()) / DAY / 365.25;
}

export function health(input: {
  conditionScore: number | null;
  openDefectSeverities: number[];
  installedOn: string | null;
  designLifeYears: number | null;
  failureProbability: number | null;
  today: Date;
}): { score: number | null; band: number | null; basis: Record<string, unknown> } {
  let inputs = 0;
  const basis: Record<string, unknown> = {};
  let start = 100;
  if (input.conditionScore != null) {
    start = Math.max(0, Math.min(100, input.conditionScore));
    basis.condition = round1(start);
    inputs++;
  }
  let defects = 0;
  if (input.openDefectSeverities.length) {
    defects = Math.min(
      60,
      input.openDefectSeverities.reduce((s, v) => s + (DEFECT_PENALTY[v] ?? 0), 0),
    );
    basis.open_defects = {
      count: input.openDefectSeverities.length,
      worst: Math.max(...input.openDefectSeverities),
      penalty: defects,
    };
    inputs++;
  }
  let age = 0;
  if (input.installedOn && input.designLifeYears && input.designLifeYears > 0) {
    const consumed = yearsBetween(input.installedOn, input.today) / input.designLifeYears;
    age = 25 * Math.max(0, Math.min(1, (consumed - 0.6) / 0.6));
    basis.life_consumed_pct = round1(consumed * 100);
    basis.age_penalty = round1(age);
    inputs++;
  }
  let predicted = 0;
  if (input.failureProbability != null) {
    const p = Math.max(0, Math.min(1, input.failureProbability));
    predicted = 30 * p;
    basis.failure_probability = Math.round(p * 1000) / 1000;
    basis.prediction_penalty = round1(predicted);
    inputs++;
  }
  if (!inputs)
    return { score: null, band: null, basis: { unassessed: "no condition, defect, age or prediction is recorded" } };
  const score = round1(Math.max(0, Math.min(100, start - defects - age - predicted)));
  return { score, band: bandOf(score), basis };
}

export function bookValue(
  cost: number,
  ageYears: number,
  lifeYears: number,
  method: DepreciationMethod,
  residualPct: number,
): number {
  const residual = (cost * residualPct) / 100;
  if (method === "none") return cost;
  if (method === "declining_balance") {
    const rate = Math.min(1, 2 / lifeYears);
    return Math.max(residual, cost * (1 - rate) ** Math.max(0, ageYears));
  }
  const used = Math.max(0, Math.min(1, ageYears / lifeYears));
  return cost - (cost - residual) * used;
}

export function lifeOf(
  a: Pick<
    AmAsset,
    | "installedOn"
    | "purchasedOn"
    | "designLifeYears"
    | "replacementValue"
    | "purchaseCost"
    | "warrantyUntil"
    | "depreciationMethod"
    | "residualPct"
  >,
  policy: AmPolicy | undefined | null,
  today: Date,
): AmLife {
  let life = a.designLifeYears;
  let lifeSource: AmLife["lifeSource"] = life != null ? "asset" : null;
  if (life == null && policy?.designLifeYears != null) {
    life = policy.designLifeYears;
    lifeSource = "policy";
  }
  let value = a.replacementValue;
  let valueSource: AmLife["valueSource"] = value != null ? "asset" : null;
  if (value == null && policy?.replacementCost != null) {
    value = policy.replacementCost;
    valueSource = "policy";
  }
  const start = a.installedOn ?? a.purchasedOn;
  const age = start ? yearsBetween(start, today) : null;
  let remaining = null;
  let consumed = null;
  let eol: string | null = null;
  if (age != null && life) {
    remaining = round1(life - age);
    consumed = round1((age / life) * 100);
    eol = new Date(new Date(start as string).getTime() + Math.round(life * 365.25) * DAY).toISOString().slice(0, 10);
  }
  const cost = a.purchaseCost ?? value;
  const book =
    cost != null && age != null && life ? bookValue(cost, age, life, a.depreciationMethod, a.residualPct) : null;
  const warranty = a.warrantyUntil ? Math.floor((new Date(a.warrantyUntil).getTime() - today.getTime()) / DAY) : null;
  return {
    designLifeYears: life,
    lifeSource,
    replacementValue: value,
    valueSource,
    ageYears: age != null ? round1(age) : null,
    remainingYears: remaining,
    consumedPct: consumed,
    endOfLife: eol,
    bookValue: book != null ? Math.round(book * 100) / 100 : null,
    warrantyDaysLeft: warranty,
  };
}

export function renewalForecast(
  lives: { life: AmLife; criticality: number }[],
  thisYear: number,
  years = 10,
): AmForecastYear[] {
  const out: AmForecastYear[] = Array.from({ length: years }, (_, i) => ({
    year: thisYear + i,
    count: 0,
    cost: 0,
    critical: 0,
    uncosted: 0,
    funded: null,
  }));
  for (const { life, criticality } of lives) {
    if (!life.endOfLife) continue;
    const idx = Math.max(0, Number(life.endOfLife.slice(0, 4)) - thisYear);
    if (idx >= years) continue;
    const b = out[idx];
    b.count++;
    if (criticality >= 4) b.critical++;
    if (life.replacementValue == null) b.uncosted++;
    else b.cost += life.replacementValue;
  }
  for (const b of out) b.cost = Math.round(b.cost * 100) / 100;
  return out;
}

export function planState(plan: Pick<AmPlan, "active" | "nextDueAt" | "leadDays">, now: Date): AmPlan["state"] {
  if (!plan.active) return "paused";
  if (!plan.nextDueAt) return "waiting";
  const due = new Date(plan.nextDueAt).getTime();
  if (due < now.getTime()) return "overdue";
  if (due - (plan.leadDays ?? 0) * DAY <= now.getTime()) return "due";
  return "scheduled";
}

export const PLAN_STATE_LABEL: Record<AmPlan["state"], string> = {
  overdue: "Overdue",
  due: "Due",
  scheduled: "Scheduled",
  waiting: "Waiting for usage",
  paused: "Paused",
};

function round1(v: number): number {
  return Math.round(v * 10) / 10;
}

/* ---- import ------------------------------------------------------------- */

/** The columns the lifecycle import understands, in the template's order. */
export const IMPORT_COLUMNS = [
  "tag",
  "manufacturer",
  "model",
  "serial_no",
  "installed_on",
  "purchased_on",
  "purchase_cost",
  "replacement_value",
  "design_life_years",
  "warranty_until",
  "lifecycle_stage",
  "criticality",
] as const;

const NUMERIC = new Set(["purchase_cost", "replacement_value", "design_life_years", "criticality"]);
const DATES = new Set(["installed_on", "purchased_on", "warranty_until"]);

/**
 * CSV text (comma or semicolon, first row the header) to import rows, with
 * the problems found per line. Unknown columns are ignored and named; a row
 * without a tag is skipped and named — silently dropping it would make an
 * import of 400 rows report 399 updated with nothing to say why.
 */
export function parseImport(text: string): { rows: AmImportRow[]; problems: string[]; ignored: string[] } {
  const lines = text
    .replace(/\r/g, "")
    .split("\n")
    .filter((l) => l.trim());
  if (!lines.length) return { rows: [], problems: [t("The file is empty.")], ignored: [] };
  const sep = lines[0].includes(";") && !lines[0].includes(",") ? ";" : ",";
  const header = splitCsv(lines[0], sep).map((h) => h.trim().toLowerCase().replace(/\s+/g, "_"));
  const known = new Set<string>(IMPORT_COLUMNS);
  const ignored = header.filter((h) => h && !known.has(h));
  if (!header.includes("tag")) return { rows: [], problems: [t("There is no 'tag' column.")], ignored };
  const rows: AmImportRow[] = [];
  const problems: string[] = [];
  lines.slice(1).forEach((line, i) => {
    const cells = splitCsv(line, sep);
    const rec: Record<string, string> = {};
    header.forEach((h, j) => (rec[h] = (cells[j] ?? "").trim()));
    if (!rec.tag) {
      problems.push(t("Line {n}: no tag.", { n: i + 2 }));
      return;
    }
    const row: AmImportRow = { tag: rec.tag };
    for (const col of IMPORT_COLUMNS) {
      const v = rec[col];
      if (col === "tag" || v === undefined || v === "") continue;
      if (NUMERIC.has(col)) {
        const n = Number(v.replace(/[, ]/g, ""));
        if (!Number.isFinite(n)) {
          problems.push(t('Line {n}: {col} "{v}" is not a number.', { n: i + 2, col, v }));
          continue;
        }
        (row as unknown as Record<string, unknown>)[camel(col)] = n;
      } else if (DATES.has(col)) {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) {
          problems.push(t('Line {n}: {col} "{v}" is not a date (YYYY-MM-DD).', { n: i + 2, col, v }));
          continue;
        }
        (row as unknown as Record<string, unknown>)[camel(col)] = v;
      } else if (col === "lifecycle_stage") {
        if (!(STAGES as string[]).includes(v)) {
          problems.push(
            t('Line {n}: lifecycle_stage "{v}" is not one of {list}.', { n: i + 2, v, list: STAGES.join(", ") }),
          );
          continue;
        }
        row.lifecycleStage = v as LifecycleStage;
      } else {
        (row as unknown as Record<string, unknown>)[camel(col)] = v;
      }
    }
    rows.push(row);
  });
  return { rows, problems, ignored };
}

function camel(s: string): string {
  return s.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase());
}

function splitCsv(line: string, sep: string): string[] {
  const out: string[] = [];
  let cur = "";
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (quoted && line[i + 1] === '"') {
        cur += '"';
        i++;
      } else quoted = !quoted;
    } else if (ch === sep && !quoted) {
      out.push(cur);
      cur = "";
    } else cur += ch;
  }
  out.push(cur);
  return out;
}

/** The patch as the platform names its fields. */
export function patchToWire(p: AmPatch): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const clear: string[] = [];
  for (const [k, v] of Object.entries(p)) {
    if (k === "attrs") {
      if (v && Object.keys(v as object).length) out.attrs = v;
      continue;
    }
    const key = k.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);
    const wire = key === "lifecycle_stage" ? "lifecycle_stage" : key;
    if (v === null) clear.push(wire);
    else if (v !== undefined) out[wire] = v;
  }
  if (clear.length) out.clear = clear;
  return out;
}
